import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Zap, Flame, Crown, ExternalLink, Loader2, Wallet, CreditCard, Landmark, Copy, CheckCircle2, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { PLATFORMS } from "@/lib/platforms";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TIERS = [
  {
    id: "push_up",
    name: "Push Up",
    icon: Zap,
    price: 20.0,
    label: "+$20 MXN",
    color: "#00F0FF",
    description: "Sube tu puja $20 MXN. Entrada rápida para competir.",
    tag: "STARTER",
  },
  {
    id: "viral_boost",
    name: "Viral Boost",
    icon: Flame,
    price: 50.0,
    label: "+$50 MXN",
    color: "#FF003C",
    description: "Salto sólido. La mejor relación para brincar al frente.",
    tag: "POPULAR",
  },
  {
    id: "god_mode",
    name: "God Mode",
    icon: Crown,
    price: null, // dynamic
    label: "Top + $100 MXN",
    color: "#FFB800",
    description: "Toma el #1 al instante con corona dorada animada.",
    tag: "TAKEOVER",
  },
];

export default function SubmitBidModal({ open, onClose, targetProfile, defaultTier = "push_up", onCompleted }) {
  const [platform, setPlatform] = useState("Instagram");
  const [handle, setHandle] = useState("");
  const [url, setUrl] = useState("");
  const [tagline, setTagline] = useState("");
  const [payoutWallet, setPayoutWallet] = useState("");
  const [tier, setTier] = useState(defaultTier);
  const [paymentMethod, setPaymentMethod] = useState("stripe");
  const [submitting, setSubmitting] = useState(false);
  const [topBid, setTopBid] = useState(0);
  const [bankInstructions, setBankInstructions] = useState(null);
  const [copied, setCopied] = useState(false);
  const [waAutoNotified, setWaAutoNotified] = useState(false);

  useEffect(() => {
    if (bankInstructions && !waAutoNotified) {
      // Auto-open WhatsApp with pre-filled message in a new tab so admin gets the notice fast
      const msg = `Hola, acabo de generar una puja por transferencia SPEI.\n\n` +
        `📌 Referencia: ${bankInstructions.reference}\n` +
        `💰 Monto: $${bankInstructions.amount.toFixed(2)} MXN\n` +
        `🏦 Banco destino: ${bankInstructions.bank_details.bank}\n` +
        `🧾 Adjunto comprobante de la transferencia.\n\n` +
        `Por favor aprueba mi puja. ¡Gracias!`;
      const waUrl = `https://wa.me/523342802102?text=${encodeURIComponent(msg)}`;
      window.open(waUrl, "_blank", "noopener,noreferrer");
      setWaAutoNotified(true);
    }
  }, [bankInstructions, waAutoNotified]);

  useEffect(() => {
    if (open) {
      setTier(defaultTier);
      setPaymentMethod("stripe");
      setBankInstructions(null);
      setWaAutoNotified(false);
      if (targetProfile) {
        setPlatform(targetProfile.platform_type);
        setHandle(targetProfile.handle_name);
        setUrl(targetProfile.profile_url);
        setTagline(targetProfile.custom_tagline);
        setPayoutWallet("");
      } else {
        setPlatform("Instagram");
        setHandle("");
        setUrl("");
        setTagline("");
        setPayoutWallet("");
      }
      api.get("/stats").then((r) => setTopBid(r.data.top_bid || 0)).catch(() => {});
    }
  }, [open, targetProfile, defaultTier]);

  const godPrice = (topBid || 0) + 100.0;

  const validate = () => {
    if (!targetProfile) {
      if (!handle.trim()) return "Handle requerido";
      if (!url.trim() || !/^https?:\/\//.test(url)) return "URL debe iniciar con http:// o https://";
      if (!tagline.trim()) return "Tagline requerido";
      if (tagline.length > 60) return "Tagline max 60 caracteres";
      if (!payoutWallet.trim() || payoutWallet.trim().length < 3) return "Payment account requerido (PayPal o cuenta bancaria)";
    } else {
      // For outbid, wallet is optional (inherits) — but if provided, must be valid length
      if (payoutWallet.trim() && payoutWallet.trim().length < 3) return "Payment account demasiado corto";
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSubmitting(true);
    try {
      const payload = {
        tier,
        payment_method: paymentMethod,
        origin_url: window.location.origin,
      };
      if (targetProfile) {
        payload.target_profile_id = targetProfile.id;
        if (payoutWallet.trim()) payload.payout_wallet = payoutWallet.trim();
      } else {
        payload.profile_data = {
          platform_type: platform,
          handle_name: handle.trim(),
          profile_url: url.trim(),
          custom_tagline: tagline.trim(),
          payout_wallet: payoutWallet.trim(),
        };
      }
      const { data } = await api.post("/payments/checkout", payload);
      if (data.payment_method === "bank_transfer") {
        setBankInstructions(data);
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Error";
      toast.error(typeof msg === "string" ? msg : "Error creando pago");
      setSubmitting(false);
    }
  };

  const copyClabe = async () => {
    try {
      await navigator.clipboard.writeText(bankInstructions.bank_details.clabe);
      setCopied(true);
      toast.success("CLABE copiada");
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  // Bank transfer instructions view
  if (bankInstructions) {
    const waMessage = `Hola, acabo de generar una puja por transferencia SPEI.\n\n` +
      `📌 Referencia: ${bankInstructions.reference}\n` +
      `💰 Monto: $${bankInstructions.amount.toFixed(2)} MXN\n` +
      `🏦 Banco destino: ${bankInstructions.bank_details.bank}\n` +
      `🧾 Adjunto comprobante de la transferencia.\n\n` +
      `Por favor aprueba mi puja. ¡Gracias!`;
    const waUrl = `https://wa.me/523342802102?text=${encodeURIComponent(waMessage)}`;

    const notifyWhatsApp = () => {
      window.open(waUrl, "_blank", "noopener,noreferrer");
    };

    return (
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent data-testid="bank-transfer-modal" className="bg-void-modal border-neon-gold/40 max-w-lg">
          <DialogHeader>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-neon-gold mb-2">
              <Landmark className="w-3.5 h-3.5" />
              PAGO POR TRANSFERENCIA
            </div>
            <DialogTitle className="font-heading text-2xl font-black tracking-tight">
              Referencia <span className="text-neon-gold">{bankInstructions.reference}</span>
            </DialogTitle>
            <DialogDescription>
              Realiza la transferencia por <span className="text-white font-bold">${bankInstructions.amount.toFixed(2)} MXN</span> (pesos mexicanos).
              Tu puja se activará después de la aprobación del admin (24-48h hábiles).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 mt-2">
            <BankField label="Banco" value={bankInstructions.bank_details.bank} />
            <BankField label="Titular" value={bankInstructions.bank_details.holder} />
            <BankField
              label="CLABE Interbancaria"
              value={bankInstructions.bank_details.clabe}
              action={
                <button
                  data-testid="copy-clabe-button"
                  onClick={copyClabe}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-neon-cyan/10 border border-neon-cyan/40 text-neon-cyan text-xs font-mono hover:bg-neon-cyan/20"
                >
                  {copied ? <CheckCircle2 className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {copied ? "Copiado" : "Copiar"}
                </button>
              }
            />
            <BankField label="Referencia (concepto)" value={bankInstructions.reference} highlight />
          </div>

          {/* WHATSAPP AUTO-NOTIFY */}
          <div className="rounded-xl border-2 border-[#25D366]/40 bg-[#25D366]/5 p-4 mt-3">
            <div className="flex items-center gap-2 mb-2">
              <MessageCircle className="w-4 h-4 text-[#25D366]" />
              <span className="font-heading font-bold text-sm">Avisa por WhatsApp para agilizar</span>
            </div>
            <p className="text-xs text-white/85 leading-relaxed mb-3">
              Al hacer clic se abrirá WhatsApp con la referencia <span className="font-mono text-[#25D366]">{bankInstructions.reference}</span>{" "}
              y el monto pre-cargados. Solo adjunta tu comprobante y envía. El admin aprobará en horas hábiles.
            </p>
            <Button
              data-testid="whatsapp-notify-button"
              onClick={notifyWhatsApp}
              className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-black font-bold rounded-full py-5"
              style={{ boxShadow: "0 0 20px rgba(37,211,102,0.4)" }}
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Enviar aviso al +52 33 4280 2102
            </Button>
          </div>

          <Button
            data-testid="bank-transfer-done-button"
            onClick={() => { setBankInstructions(null); onClose(); onCompleted?.(); }}
            variant="outline"
            className="w-full border-white/15 hover:border-white/40 font-bold rounded-full py-5 mt-2"
          >
            Entendido, ya guardé la referencia
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="submit-bid-modal"
        className="bg-void-modal border-white/10 max-w-2xl max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neon-cyan mb-2">
            {targetProfile ? "OUTBID & TAKE OVER" : "SUBMIT YOUR PROFILE"}
          </div>
          <DialogTitle className="font-heading text-2xl sm:text-3xl font-black tracking-tight">
            {targetProfile ? `Vence a ${targetProfile.handle_name}` : "Puja para tomar el trono"}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Elige tu plataforma, pega tu URL y selecciona una tier. Se procesa vía Stripe (checkout seguro).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {!targetProfile && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                    Platform
                  </Label>
                  <Select value={platform} onValueChange={setPlatform}>
                    <SelectTrigger data-testid="platform-select" className="mt-1.5 bg-black border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-void-card border-white/10">
                      {PLATFORMS.map((p) => (
                        <SelectItem key={p} value={p} data-testid={`platform-option-${p}`}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                    Handle
                  </Label>
                  <Input
                    data-testid="handle-input"
                    value={handle}
                    onChange={(e) => setHandle(e.target.value)}
                    placeholder="@usuario"
                    maxLength={60}
                    className="mt-1.5 bg-black border-white/10 focus-visible:ring-neon-cyan"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                  Profile URL
                </Label>
                <Input
                  data-testid="url-input"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://instagram.com/tu_perfil"
                  className="mt-1.5 bg-black border-white/10 focus-visible:ring-neon-cyan"
                />
              </div>

              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">
                  Catchy Tagline
                  <span className="ml-2 text-white/40">{tagline.length}/60</span>
                </Label>
                <Input
                  data-testid="tagline-input"
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value.slice(0, 60))}
                  placeholder="Chef vegano que vive en el metro CDMX"
                  maxLength={60}
                  className="mt-1.5 bg-black border-white/10 focus-visible:ring-neon-cyan"
                />
              </div>

              <div className="rounded-xl border border-neon-gold/30 bg-neon-gold/5 p-4">
                <Label className="text-xs font-mono uppercase tracking-widest text-neon-gold flex items-center gap-2">
                  <Wallet className="w-3.5 h-3.5" />
                  Your Payment Account (for the Jackpot)
                </Label>
                <Input
                  data-testid="payout-wallet-input"
                  value={payoutWallet}
                  onChange={(e) => setPayoutWallet(e.target.value)}
                  placeholder="Enter PayPal email or Bank routing"
                  className="mt-1.5 bg-black border-neon-gold/30 focus-visible:ring-neon-gold"
                />
                <p className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                  Si ganas el jackpot (50% del pozo), recibirás el pago aquí. Se guarda cifrado.
                </p>
              </div>
            </>
          )}

          {targetProfile && (
            <div className="rounded-xl border border-neon-gold/30 bg-neon-gold/5 p-4">
              <Label className="text-xs font-mono uppercase tracking-widest text-neon-gold flex items-center gap-2">
                <Wallet className="w-3.5 h-3.5" />
                Payment Account (opcional)
              </Label>
              <Input
                data-testid="payout-wallet-input"
                value={payoutWallet}
                onChange={(e) => setPayoutWallet(e.target.value)}
                placeholder="Enter PayPal email or Bank routing (o deja vacío para heredar)"
                className="mt-1.5 bg-black border-neon-gold/30 focus-visible:ring-neon-gold"
              />
              <p className="text-[11px] text-muted-foreground mt-2">
                Actualiza el payout wallet del perfil si te lo quedas.
              </p>
            </div>
          )}

          {/* Tier Selection */}
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3 block">
              Pick your tier
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {TIERS.map((t) => {
                const active = tier === t.id;
                const price = t.id === "god_mode" ? godPrice : t.price;
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`tier-${t.id}`}
                    onClick={() => setTier(t.id)}
                    className={`text-left p-4 rounded-xl border transition-all relative overflow-hidden ${
                      active
                        ? "border-2"
                        : "border border-white/10 hover:border-white/25"
                    }`}
                    style={{
                      borderColor: active ? t.color : undefined,
                      boxShadow: active ? `0 0 20px ${t.color}44` : undefined,
                      background: active ? `${t.color}0A` : "#0A0A0A",
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <t.icon className="w-4 h-4" style={{ color: t.color }} />
                      <span className="font-heading font-bold text-sm">{t.name}</span>
                    </div>
                    <div className="text-[9px] font-mono uppercase tracking-widest mb-3" style={{ color: t.color }}>
                      {t.tag}
                    </div>
                    <div className="font-mono text-lg font-bold" style={{ color: t.color }}>
                      ${price.toFixed(2)}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-2 leading-relaxed">
                      {t.description}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <p className="text-[11px] text-muted-foreground font-mono uppercase tracking-widest">
            All links open in a new tab. Higher bids guarantee top visibility.
          </p>

          {/* PAYMENT METHOD */}
          <div>
            <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-3 block">
              Método de pago
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                data-testid="payment-method-stripe"
                onClick={() => setPaymentMethod("stripe")}
                className={`text-left p-3 rounded-xl border transition-all ${
                  paymentMethod === "stripe"
                    ? "border-neon-cyan bg-neon-cyan/5"
                    : "border-white/10 hover:border-white/25 bg-black"
                }`}
                style={paymentMethod === "stripe" ? { boxShadow: "0 0 15px rgba(0,240,255,0.3)" } : {}}
              >
                <div className="flex items-center gap-2 mb-1">
                  <CreditCard className="w-4 h-4 text-neon-cyan" />
                  <span className="font-heading font-bold text-sm">Tarjeta / Stripe</span>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  Débito, crédito o Apple/Google Pay. Instantáneo.
                </div>
              </button>
              <button
                type="button"
                data-testid="payment-method-bank"
                onClick={() => setPaymentMethod("bank_transfer")}
                className={`text-left p-3 rounded-xl border transition-all ${
                  paymentMethod === "bank_transfer"
                    ? "border-neon-gold bg-neon-gold/5"
                    : "border-white/10 hover:border-white/25 bg-black"
                }`}
                style={paymentMethod === "bank_transfer" ? { boxShadow: "0 0 15px rgba(255,184,0,0.3)" } : {}}
              >
                <div className="flex items-center gap-2 mb-1">
                  <Landmark className="w-4 h-4 text-neon-gold" />
                  <span className="font-heading font-bold text-sm">Transferencia SPEI</span>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  BBVA México. Aprobación manual 24-48h.
                </div>
              </button>
            </div>
          </div>

          <Button
            data-testid="confirm-bid-button"
            disabled={submitting}
            onClick={handleSubmit}
            className="w-full bg-neon-cyan hover:bg-neon-cyan/90 text-black font-bold rounded-full py-6 text-base neon-cyan-glow"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Procesando...
              </>
            ) : paymentMethod === "stripe" ? (
              <>
                Pagar con Stripe <ExternalLink className="w-4 h-4 ml-2" />
              </>
            ) : (
              <>
                Ver datos bancarios <Landmark className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BankField({ label, value, action, highlight }) {
  return (
    <div className={`rounded-lg border p-3 flex items-center justify-between gap-3 ${highlight ? "border-neon-gold/40 bg-neon-gold/5" : "border-white/10 bg-black/50"}`}>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
          {label}
        </div>
        <div className={`font-mono text-sm truncate ${highlight ? "text-neon-gold font-bold" : "text-white"}`}>
          {value}
        </div>
      </div>
      {action}
    </div>
  );
}
